/**
 * RTK Query endpoints for conversation management via the agents API.
 * Handles CRUD operations for conversations.
 */

import type {
  ConversationCreateRequest,
  ConversationListParams,
  ConversationListResponse,
  ConversationResponse,
  PinConversationRequest,
  RenameConversationRequest,
} from '../interfaces/conversation.interface'
import { agentsApiSlice } from './agents-api.store'

type ConversationTagType = 'Conversations' | 'Conversation'
type ConversationTag = { type: 'Conversation'; id: string } | ConversationTagType

const API_BASE = '/api/v1/conversations'

const buildParams = (params?: ConversationListParams): Record<string, number> | undefined =>
  params ? { page: params.page ?? 1, page_size: params.page_size ?? 50 } : undefined

const buildListTags = (response: ConversationListResponse | undefined): ConversationTag[] =>
  response
    ? [...response.items.map(({ id }) => ({ type: 'Conversation' as const, id })), 'Conversations']
    : ['Conversations']

const invalidateConversationTags = (conversationId: string): ConversationTag[] => [
  { type: 'Conversation', id: conversationId },
  'Conversations',
]

export const conversationAgentsEndpoints = agentsApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgentConversations: builder.query<ConversationListResponse, ConversationListParams | void>({
      query: (params) => ({ url: API_BASE, params: buildParams(params ?? undefined) }),
      providesTags: (response) => buildListTags(response),
    }),

    getAgentConversation: builder.query<ConversationResponse, string>({
      query: (conversationId) => `${API_BASE}/${conversationId}`,
      providesTags: (_, __, conversationId) => [{ type: 'Conversation', id: conversationId }],
    }),

    createAgentConversation: builder.mutation<ConversationResponse, ConversationCreateRequest>({
      query: (body) => ({ url: API_BASE, method: 'POST', body }),
      invalidatesTags: ['Conversations'],
    }),

    renameAgentConversation: builder.mutation<
      ConversationResponse,
      { conversationId: string; payload: RenameConversationRequest }
    >({
      query: ({ conversationId, payload }) => ({
        url: `${API_BASE}/${conversationId}/name`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_, __, { conversationId }) => invalidateConversationTags(conversationId),
    }),

    deleteAgentConversation: builder.mutation<void, string>({
      query: (conversationId) => ({ url: `${API_BASE}/${conversationId}`, method: 'DELETE' }),
      invalidatesTags: (_, __, conversationId) => invalidateConversationTags(conversationId),
    }),

    pinAgentConversation: builder.mutation<
      ConversationResponse,
      { conversationId: string; payload: PinConversationRequest }
    >({
      query: ({ conversationId, payload }) => ({
        url: `${API_BASE}/${conversationId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_, __, { conversationId }) => invalidateConversationTags(conversationId),
    }),
  }),
})

export const {
  useGetAgentConversationsQuery,
  useGetAgentConversationQuery,
  useLazyGetAgentConversationQuery,
  useCreateAgentConversationMutation,
  useRenameAgentConversationMutation,
  useDeleteAgentConversationMutation,
  usePinAgentConversationMutation,
} = conversationAgentsEndpoints
